import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import InputPassword from '@/components/InputPassword';
import { authApi } from './auth-api';
import { mapAuthError } from './auth-error-map';
import MagicLinkButton from './MagicLinkButton';

// ─── Component ────────────────────────────────────────────────────────────────

export default function AuthLoginCredentials({
    email,
    redirectPath,
    oauthQuery,
    onBack,
    onUseAnotherAccount,
    onSuccess,
    lockEmail = false,
}: {
    email: string;
    redirectPath: string;
    oauthQuery?: string;
    onBack: () => void;
    onUseAnotherAccount?: () => void;
    onSuccess: () => void;
    lockEmail?: boolean;
}) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [loadingPassword, setLoadingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const passwordRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Auto-focus password on mount
        const timer = setTimeout(() => passwordRef.current?.focus(), 50);
        return () => clearTimeout(timer);
    }, []);

    const onSubmitPassword = async (event: FormEvent) => {
        event.preventDefault();
        setPasswordError(null);
        setLoadingPassword(true);
        try {
            const result = await authApi.signInEmail({
                email,
                password,
                callbackURL: redirectPath,
                oauthQuery,
            });

            // 2FA checks must take priority over the generic redirect — better-auth
            // issues a redirect:true + url:"/home" even when 2FA is required, so
            // checking redirect first would send the user to /home immediately.
            if (result.twoFactorRedirect) {
                navigate('/auth/two-factor', {
                    state: { redirectPath, oauthQuery, methods: result.twoFactorMethods, email },
                });
                return;
            }

            // Read the re-enrollment flag directly from the sign-in response body —
            // better-auth includes additionalFields in the sign-in user payload,
            // so this avoids a second round-trip and any async timing ambiguity.
            if (result.user?.requiresTwoFactorReenrollment) {
                navigate('/auth/two-factor-setup', { state: { redirectPath, password, oauthQuery } });
                return;
            }

            if (result.redirect && result.url) {
                window.location.assign(result.url);
                return;
            }

            onSuccess();
        } catch (error) {
            const mapped = mapAuthError(error, t, 'login');
            if (mapped.field === 'password') {
                setPasswordError(mapped.message);
            } else {
                enqueueSnackbar(mapped.message, { variant: 'error' });
            }
        } finally {
            setLoadingPassword(false);
        }
    };

    return (
        <Stack spacing={2}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" fontWeight={700}>
                    {t('auth.login.welcome-back')}
                </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
                {!lockEmail && (
                    <Tooltip title={t('auth.login.back')}>
                        <IconButton onClick={onBack} color="primary" sx={{ ml: -0.5 }}>
                            <ArrowBackRounded color="primary" />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Email chip — confirms who they're signing in as */}
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                        {t('auth.step.email.signing-in-as')}
                    </Typography>
                    <Chip
                        label={email}
                        onClick={lockEmail ? undefined : onBack}
                        variant="outlined"
                        size="small"
                        sx={{
                            fontWeight: 500,
                            cursor: lockEmail ? 'default' : 'pointer',
                            borderRadius: 'var(--mui-shape-sm)',
                        }}
                    />
                    {!lockEmail && onUseAnotherAccount && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={onUseAnotherAccount}
                            data-id="login-use-another-account"
                            sx={{ display: 'block', px: 0, mt: 0.5, minWidth: 0 }}
                        >
                            {t('auth.login.use-another-account')}
                        </Button>
                    )}
                </Box>
            </Stack>

            {/* Password form */}
            <Stack component="form" spacing={2} onSubmit={onSubmitPassword}>
                <InputPassword
                    label={t('str.password')}
                    value={password}
                    onChange={(e) => {
                        setPassword((e as React.ChangeEvent<HTMLInputElement>).target.value);
                        if (passwordError) setPasswordError(null);
                    }}
                    name="password"
                    error={!!passwordError}
                    helperText={passwordError ?? undefined}
                    inputRef={passwordRef}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                    <Typography
                        component={Link}
                        to="/auth/forgot-password"
                        variant="caption"
                        color="primary.main"
                        sx={{ textDecoration: 'none', fontWeight: 500 }}
                    >
                        {t('auth.login.forgot-password')}
                    </Typography>
                </Box>

                <Button type="submit" variant="contained" size="large" fullWidth disabled={loadingPassword}>
                    {loadingPassword ? t('auth.common.loading') : t('auth.login.submit')}
                </Button>
            </Stack>

            {/* Magic link section */}
            <Divider>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                    {t('auth.common.or')}
                </Typography>
            </Divider>

            <MagicLinkButton
                email={email}
                redirectPath={redirectPath}
                oauthQuery={oauthQuery}
                variant="outlined"
            />
        </Stack>
    );
}
